import { useState } from "react";
import { CircleCheck, Loader2, Eraser, Scale } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { clearTare, connectScale, tareScale, useScale } from "@/lib/scale";
import { round3, weighPrice } from "@/lib/weigh";
import { fmtNum } from "@/lib/format";
import { BigButton, Chip, inputCls } from "@/components/ui-kit";

export default function WeighPanel({ product, onConfirm, confirmLabel, confirmIcon: Icon }) {
  const { t, money, view } = useApp();
  const s = useScale();
  const live = s.status === "connected";
  const [manual, setManual] = useState("");
  const [unit, setUnit] = useState("kg");
  const useManual = !live || manual !== "";
  const manualKg = round3((Number(manual.replace(",", ".")) || 0) / (unit === "g" ? 1000 : 1));
  const kg = useManual ? manualKg : round3(s.kg - s.tare);
  const total = kg > 0 ? weighPrice(kg, product.price, view.shop) : 0;
  const ready = kg > 0 && (useManual || s.stable);

  return (
    <div className="space-y-3" data-testid="weigh-panel">
      <div className={`rounded-2xl p-4 text-center ${live && !useManual ? "bg-[#0B2A5B] text-white" : "bg-secondary"}`}>
        <div className="text-xs font-bold uppercase opacity-70 flex items-center justify-center gap-1.5">
          <Scale className="w-4 h-4" />{live ? (s.sim ? t("scale_sim") : t("scale_live")) : t("scale_manual")}
        </div>
        <div className="text-5xl font-black tracking-tight my-1" dir="ltr" data-testid="weigh-kg">{kg.toFixed(3)} <span className="text-2xl">kg</span></div>
        {live && !useManual && (
          <div className={`inline-flex items-center gap-1 text-sm font-bold ${s.stable ? "text-[#5EE3A1]" : "text-[#FFB547]"}`} data-testid="weigh-stable" data-stable={s.stable}>
            {s.stable ? <CircleCheck className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />}{s.stable ? t("scale_stable") : t("scale_settling")}
            {s.tare > 0 && <span className="ms-2 opacity-80" dir="ltr">T {s.tare.toFixed(3)}</span>}
          </div>
        )}
      </div>
      {live ? (
        <div className="grid grid-cols-2 gap-2">
          <BigButton variant="soft" onClick={tareScale} data-testid="weigh-tare-btn">{t("scale_tare")}</BigButton>
          <BigButton variant="soft" onClick={clearTare} disabled={!s.tare} data-testid="weigh-clear-tare-btn"><Eraser className="w-5 h-5" />{t("scale_clear_tare")}</BigButton>
        </div>
      ) : (
        <button type="button" onClick={connectScale} className="w-full text-sm font-bold text-primary underline" data-testid="weigh-connect-link">{t("scale_connect")}</button>
      )}
      {s.status === "unsupported" && <p className="text-xs text-muted-foreground" data-testid="weigh-unsupported">{t("scale_unsupported")}</p>}
      <div className="flex gap-2 items-center">
        <input dir="ltr" inputMode="decimal" value={manual} onChange={(e) => setManual(e.target.value)} placeholder={t("scale_type_weight")} className={inputCls} data-testid="weigh-manual-input" />
        <Chip active={unit === "kg"} onClick={() => setUnit("kg")} data-testid="weigh-unit-kg">kg</Chip>
        <Chip active={unit === "g"} onClick={() => setUnit("g")} data-testid="weigh-unit-g">g</Chip>
      </div>
      <div className="rounded-xl border border-border p-3 flex justify-between items-end">
        <span className="text-sm text-muted-foreground" dir="ltr">{fmtNum(product.price)} / kg × {kg.toFixed(3)}</span>
        <span className="text-3xl font-extrabold" data-testid="weigh-total">{money(total)}</span>
      </div>
      <BigButton disabled={!ready} onClick={() => onConfirm(kg, total)} className="w-full" data-testid="weigh-confirm-btn">{Icon && <Icon className="w-5 h-5" />}{confirmLabel}</BigButton>
    </div>
  );
}
